/**
 * 017: structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/invoice/invoice-list/filters.tsx — its filter
 * elements: a `StatusField` and `DateFiled`, inline on a wide toolbar with the labels hidden, full-
 * width and labelled inside the drawer. Two separate `DateFiled`s (from/to), not one
 * `selectsRange` field — see orders/filters.tsx.
 *
 * Adapted for server-side filtering: every control writes the route's search params (irn, from, to)
 * rather than a TanStack column filter, because the loader re-fetches the page on every navigation.
 * IRN status is a fixed vocabulary, so it is a select rather than free text. One control the vendor
 * file has is left out — `PriceField` (no service parameter to filter an amount on). The search
 * (invoice OR order number) and the state dropdown are the toolbar's own, in List.tsx: Billing matches
 * server-side (`InvoiceStore.SearchAsync`'s `.Contains`, plus the orders Ordering matched), so it
 * searches every invoice, not the loaded page.
 */
import { useNavigate } from "react-router-dom";
import { StatusField } from "@steelyard/theme";
import { FILTER_LABEL } from "../../components/table/toolbar";
import { DateField } from "../../components/date-field";
import { IRN_STATE, statusWords } from "../../lib/services/invoicing";
import { parseIsoDate, toIsoDate } from "../../lib/format";

export type InvoiceFilterValues = { state?: string; irn?: string; invoiceNumber?: string; from?: string; to?: string };

/**
 * The exact vocabulary FilterTabs carried — a rule, not a label (BR state names). State is the
 * status dropdown on the toolbar (List.tsx), not a drawer field — one control per filter.
 */
export const INVOICE_STATES = [
  // Billing's `InvoiceState`, all six, in lifecycle order.
  { value: "raised", label: "Raised" },
  { value: "registered", label: "Registered" },
  { value: "registration_failed", label: "Registration failed" },
  { value: "queued", label: "Queued" },
  { value: "posted", label: "Posted" },
  { value: "cancelled", label: "Cancelled" },
];

/** Every IRN status the list's own badge knows (`IRN_STATE`), worded the way the badge words it. */
const IRN_OPTIONS = Object.keys(IRN_STATE).map((value) => ({ value, label: statusWords(value) }));


export function Filters({
  value,
  query,
}: {
  value: Pick<InvoiceFilterValues, "irn" | "from" | "to">;
  query: (over: Record<string, string | undefined>) => string;
}) {
  const navigate = useNavigate();
  const { irn, from, to } = value;

  // Every control resets the page — a filter change means a different result set (plan step: R1).
  const apply = (over: InvoiceFilterValues) => navigate(query({ page: undefined, ...over }));

  const fromDate = parseIsoDate(from ?? null);
  const toDate = parseIsoDate(to ?? null);

  return (
    <>
      <StatusField
        options={IRN_OPTIONS}
        value={IRN_OPTIONS.find((option) => option.value === irn) ?? null}
        onChange={(option: { value: string } | null) => apply({ irn: option?.value })}
        clearable
        onClear={() => apply({ irn: undefined })}
        placeholder="Any IRN status"
        label="IRN status"
        labelClassName={FILTER_LABEL}
        dropdownClassName="!z-10 h-auto"
        className="w-full @5xl:w-48"
      />
      {/* Two independent pickers, not one range field — see orders/filters.tsx for why:
          `suffix: null` drops the dropdown-caret icon (the calendar prefix is the only "open"
          affordance), and clearing is rizzui's own `clearable`/`onClear`, not react-datepicker's
          `isClearable` (a hard-coded blue circle that doesn't match this app's inputs). */}
      <DateField
        dateFormat="dd-MMM-yyyy"
        className="w-full @5xl:w-48"
        placeholderText="Invoiced from…"
        selected={fromDate}
        maxDate={toDate ?? undefined}
        onChange={(date) => apply({ from: date ? toIsoDate(date as Date) : undefined })}
        inputProps={{
          label: "Invoiced from",
          labelClassName: FILTER_LABEL,
          suffix: null,
          clearable: Boolean(fromDate),
          onClear: () => apply({ from: undefined }),
        }}
      />
      <DateField
        dateFormat="dd-MMM-yyyy"
        className="w-full @5xl:w-48"
        placeholderText="Invoiced to…"
        selected={toDate}
        minDate={fromDate ?? undefined}
        onChange={(date) => apply({ to: date ? toIsoDate(date as Date) : undefined })}
        inputProps={{
          label: "Invoiced to",
          labelClassName: FILTER_LABEL,
          suffix: null,
          clearable: Boolean(toDate),
          onClear: () => apply({ to: undefined }),
        }}
      />
    </>
  );
}
